"use client";

import { useEffect, useState, useCallback } from "react";
import {
  MoreHorizontal,
  Pencil,
  Trash2,
  GitBranch,
  Monitor,
  Send,
  MessageSquare,
  Webhook,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslations } from "next-intl";
import { Link, useRouter } from "@/i18n/navigation";
import { api } from "@/lib/api";
import type { RouteItem, Source, Target } from "@/lib/types/routing";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const TARGET_ICONS: Record<string, typeof Monitor> = {
  Mt5: Monitor,
  TelegramChannel: Send,
  DiscordWebhook: MessageSquare,
  HttpWebhook: Webhook,
};

export function FlowsList() {
  const t = useTranslations();
  const router = useRouter();
  const [flows, setFlows] = useState<RouteItem[]>([]);
  const [sources, setSources] = useState<Map<string, Source>>(new Map());
  const [targets, setTargets] = useState<Map<string, Target>>(new Map());
  const [loading, setLoading] = useState(true);

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [routeList, sourceList, targetList] = await Promise.all([
        api.routes.list(),
        api.sources.list(),
        api.targets.list(),
      ]);
      setFlows(routeList);
      setSources(new Map(sourceList.map((s) => [s.id, s])));
      setTargets(new Map(targetList.map((tg) => [tg.id, tg])));
    } catch {
      toast.error(t("dashboard.routes.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleActive = async (flow: RouteItem) => {
    try {
      await api.routes.update(flow.id, { isActive: !flow.isActive });
      setFlows((prev) =>
        prev.map((r) =>
          r.id === flow.id ? { ...r, isActive: !flow.isActive } : r
        )
      );
    } catch {
      toast.error(t("dashboard.routes.updateFailed"));
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await api.routes.delete(deleteId);
      setFlows((prev) => prev.filter((r) => r.id !== deleteId));
      toast.success(t("dashboard.routes.routeDeleted"));
      setDeleteId(null);
    } catch {
      toast.error(t("dashboard.routes.deleteFailed"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-muted-foreground">{t("dashboard.routes.subtitle")}</p>
        <Button asChild>
          <Link href="/flows/new">{t("dashboard.routes.newButton")}</Link>
        </Button>
      </div>

      {/* Cards / Empty state */}
      {loading ? null : flows.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-4 rounded-2xl border border-dashed border-border py-20 text-center">
          <GitBranch className="h-10 w-10 text-muted-foreground/40" aria-hidden="true" />
          <div>
            <p className="font-semibold">{t("dashboard.routes.emptyTitle")}</p>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">{t("dashboard.routes.emptyBody")}</p>
          </div>
          <Button asChild>
            <Link href="/flows/new">{t("dashboard.routes.emptyCta")}</Link>
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {flows.map((flow) => {
            const source = sources.get(flow.sourceId);
            const flowTargets = flow.targetIds
              .map((tid) => targets.get(tid))
              .filter((tg): tg is Target => tg !== undefined);
            const shown = flowTargets.slice(0, 2);
            const extra = flow.targetIds.length - shown.length;
            return (
              <Card key={flow.id} className="transition-colors hover:border-primary/40">
                <CardContent className="space-y-3 pt-6">
                  {/* Mini source-hub-destination diagram, pinned LTR like the flow map. */}
                  <div dir="ltr" className="flex items-center gap-2">
                    <span className="max-w-[130px] truncate rounded-lg border border-border bg-muted/40 px-2.5 py-1.5 text-[11.5px] font-semibold">
                      {source?.name ?? flow.sourceId.slice(0, 8)}
                    </span>
                    <span className="h-0.5 min-w-3 flex-1 rounded bg-gradient-to-r from-primary to-secondary opacity-50" />
                    <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full border border-primary/50 bg-primary/15">
                      <Zap className="h-3 w-3 text-primary" aria-hidden="true" />
                    </span>
                    <span className="h-0.5 min-w-3 flex-1 rounded bg-gradient-to-r from-primary to-secondary opacity-50" />
                    <span className="flex min-w-0 flex-col gap-1">
                      {shown.length === 0 && (
                        <span className="max-w-[130px] truncate rounded-lg border border-border bg-muted/40 px-2.5 py-1.5 text-[11.5px] font-semibold">
                          ?
                        </span>
                      )}
                      {shown.map((tg) => {
                        const Icon = TARGET_ICONS[tg.kind] ?? Monitor;
                        return (
                          <span
                            key={tg.id}
                            className="inline-flex max-w-[130px] items-center gap-1 rounded-lg border border-border bg-muted/40 px-2.5 py-1 text-[11.5px] font-semibold"
                          >
                            <Icon className="h-3 w-3 flex-none text-muted-foreground" aria-hidden="true" />
                            <span className="truncate">{tg.name}</span>
                          </span>
                        );
                      })}
                      {extra > 0 && (
                        <span className="text-[11px] text-muted-foreground">
                          {t("dashboard.home.moreTargets", { count: extra })}
                        </span>
                      )}
                    </span>
                  </div>

                  {/* Name, rules count, controls */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <Link
                        href={`/flows/${flow.id}`}
                        className="block truncate font-medium hover:underline"
                      >
                        {flow.name}
                      </Link>
                      <p className="text-xs text-muted-foreground">
                        {t("dashboard.routes.rulesCount", { count: flow.steps.length })}
                      </p>
                    </div>
                    <div className="flex flex-none items-center gap-1.5">
                      <Switch
                        checked={flow.isActive}
                        onCheckedChange={() => toggleActive(flow)}
                        aria-label={flow.isActive ? t("dashboard.routes.active") : t("dashboard.routes.inactive")}
                      />
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon-xs" aria-label={t("dashboard.routes.actionsAria")}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => router.push(`/flows/${flow.id}`)}>
                            <Pencil className="h-4 w-4" />
                            {t("dashboard.routes.edit")}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => setDeleteId(flow.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                            {t("dashboard.routes.delete")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Delete confirm dialog */}
      <Dialog
        open={!!deleteId}
        onOpenChange={(open) => !open && setDeleteId(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("dashboard.routes.deleteRoute")}</DialogTitle>
            <DialogDescription>{t("dashboard.routes.deleteConfirm")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>
              {t("dashboard.routes.cancel")}
            </Button>
            <Button
              variant="destructive"
              disabled={deleting}
              onClick={handleDelete}
            >
              {deleting ? t("dashboard.routes.saving") : t("dashboard.routes.deleteButton")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
